import React, { useEffect, useState } from "react";
import { database, ref, push, onValue, remove, update } from "./firebase";
import "./BillingSystem.css";
import Navigation from "./Navigation";
import BossNavigation from "./BossNavigation";

export default function Expenses() {
  const [entries, setEntries] = useState([]);

  const [title, setTitle] = useState("");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [cash, setCash] = useState("");
  const [online, setOnline] = useState("");
  const [expenseFrom, setExpenseFrom] = useState("BOTTLERS");
  const [editId, setEditId] = useState(null);

  const [filter, setFilter] = useState("ALL");

  // NEW: DATE RANGE
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  useEffect(() => {
    onValue(ref(database, "expensesHistory"), (snap) => {
      if (!snap.exists()) {
        setEntries([]);
        return;
      }
      const list = Object.keys(snap.val()).map((id) => ({
        id,
        ...snap.val()[id],
      }));
      list.sort((a, b) => new Date(b.date) - new Date(a.date));
      setEntries(list);
    });
  }, []);

  const totalAmount = Number(cash || 0) + Number(online || 0);

  const handleSave = () => {
    if (!title.trim()) {
      alert("Enter expense name");
      return;
    }

    const record = {
      title,
      expenseFrom,
      date,
      cashAmount: Number(cash || 0),
      onlineAmount: Number(online || 0),
      totalAmount,
      createdAt: Date.now(),
    };

    if (editId) {
      update(ref(database, `expensesHistory/${editId}`), record);
      alert("Updated");
    } else {
      push(ref(database, "expensesHistory"), record);
      alert("Saved");
    }

    setTitle("");
    setCash("");
    setOnline("");
    setExpenseFrom("BOTTLERS");
    setEditId(null);
  };

  const handleEdit = (e) => {
    setEditId(e.id);
    setTitle(e.title);
    setExpenseFrom(e.expenseFrom || "BOTTLERS");
    setDate(e.date);
    setCash(e.cashAmount || "");
    setOnline(e.onlineAmount || "");
  };

  const handleDelete = (id) => {
    if (window.confirm("Delete this expense?")) {
      remove(ref(database, `expensesHistory/${id}`));
    }
  };

  // FILTER LOGIC (CATEGORY + DATE RANGE)
  const filteredEntries = entries.filter((e) => {
    const categoryMatch = filter === "ALL" || e.expenseFrom === filter;

    const fromMatch = !fromDate || new Date(e.date) >= new Date(fromDate);
    const toMatch = !toDate || new Date(e.date) <= new Date(toDate);

    return categoryMatch && fromMatch && toMatch;
  });

  // GROUP BY DATE
  const grouped = filteredEntries.reduce((acc, e) => {
    const key = e.date || "No Date";
    if (!acc[key]) acc[key] = [];
    acc[key].push(e);
    return acc;
  }, {});

  // CATEGORY TOTALS
  const totals = filteredEntries.reduce((acc, e) => {
    const key = e.expenseFrom || "OTHER";
    acc[key] = (acc[key] || 0) + (e.totalAmount || 0);
    return acc;
  }, {});

  // MONTHLY SUMMARY
  const monthlyTotals = filteredEntries.reduce((acc, e) => {
    const month = e.date ? e.date.slice(0, 7) : "Unknown"; // YYYY-MM
    acc[month] = (acc[month] || 0) + (e.totalAmount || 0);
    return acc;
  }, {});

  return (
    <div className="history-container">
      <Navigation role="special manager" />
      <BossNavigation role="boss"/>

      <h2 className="page-title">💸 Expenses</h2>

      <div className="entry-controls">
        <input
          type="text"
          placeholder="Expense Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="input-text"
        />

        <select
          value={expenseFrom}
          onChange={(e) => setExpenseFrom(e.target.value)}
          className="input-text"
        >
          <option value="BOTTLERS">BOTTLERS</option>
          <option value="ENTERPRISES">ENTERPRISES</option>
          <option value="POUCH">POUCH</option>
          <option value="LOCAL">LOCAL</option>
        </select>

        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="input-date"
        />

        <input
          type="number"
          placeholder="Cash ₹"
          value={cash}
          onChange={(e) => setCash(e.target.value)}
          className="input-small"
        />

        <input
          type="number"
          placeholder="Online ₹"
          value={online}
          onChange={(e) => setOnline(e.target.value)}
          className="input-small"
        />

        <button className="save-btn" onClick={handleSave}>
          {editId ? "Update" : "Save"}
        </button>
      </div>

      <h3 className="section-title">🔍 Filter</h3>
      <div style={{ marginBottom: "10px" }}>
        {["ALL", "BOTTLERS", "ENTERPRISES", "POUCH", "LOCAL"].map((f) => (
          <button key={f} onClick={() => setFilter(f)} style={{ marginRight: "5px" }}>
            {f}
          </button>
        ))}
      </div>

      {/* DATE RANGE FILTER */}
      <h3 className="section-title">📅 Date Range</h3>
      <div style={{ marginBottom: "10px" }}>
        <input
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
        />
        <span> to </span>
        <input
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
        />
        <button onClick={() => { setFromDate(""); setToDate(""); }}>
          Clear
        </button>
      </div>

      <h3 className="section-title">📊 Category Totals</h3>
      <div>
        {Object.keys(totals).map((key) => (
          <p key={key}>
            <b>{key}:</b> ₹{totals[key]}
          </p>
        ))}
      </div>

     

      <h3 className="section-title">📜 Expense History </h3>

      {Object.keys(grouped).map((dateKey) => {
        const dayTotal = grouped[dateKey].reduce((sum, e) => sum + (e.totalAmount || 0), 0);

        return (
          <div key={dateKey} className="history-table-section">
            <h4 className="date-header">📅 {dateKey} | Total: ₹{dayTotal}</h4>

            <table className="styled-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>From</th>
                  <th>Cash</th>
                  <th>Online</th>
                  <th>Total</th>
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
                {grouped[dateKey].map((e) => (
                  <tr key={e.id}>
                    <td>{e.title}</td>
                    <td>{e.expenseFrom}</td>
                    <td>₹{e.cashAmount}</td>
                    <td>₹{e.onlineAmount}</td>
                    <td>₹{e.totalAmount}</td>
                    <td>
                      <button onClick={() => handleEdit(e)}>Edit</button>
                      <button onClick={() => handleDelete(e.id)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}